import { useState, useEffect, type ComponentType, type SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Home, PhoneCall, Calendar, TrendingUp, Users, Settings, HelpCircle, ChevronLeft, MessageSquare } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BRAND } from '@/config/brand';
// "Contacter le support" (2026-09-22): the portal has no other chrome, so the
// support button lives here, under the other bottom entries.
import { ContactKevinModal } from '@/components/common/ContactKevinButton';

/**
 * The client portal's sidebar on the flag-off path (the rail, PortalRail,
 * replaces it behind ui_refonte; both read getNavItems / getBottomItems).
 *
 * W5 (B3-ClientDashboard):
 *   - `variant="drawer"`: the same entries in the menu drawer under 1024 px
 *     (there was no navigation at all below `lg`);
 *   - « Paramètres » is shown: the portal only admits viewers, and the entry
 *     was filtered to tenant admins, so nobody ever saw it. The settings tab
 *     now holds the client's own profile and preferences (sections W5);
 *   - the width no longer depends on the tab (it was folded on Accueil only).
 */

type IconType = ComponentType<SVGProps<SVGSVGElement> & { className?: string }>;

interface NavItem {
    id: string;
    label: string;
    icon: IconType;
    badge?: unknown;
}

export function getNavItems(t: (key: string) => string): NavItem[] {
    return [
        { id: 'home', label: t('admin:backoffice.sidebar.home'), icon: Home },
        { id: 'performance', label: t('admin:backoffice.sidebar.performance'), icon: TrendingUp },
        { id: 'calls', label: t('admin:backoffice.sidebar.calls'), icon: PhoneCall, badge: null },
        { id: 'prospects', label: t('admin:backoffice.sidebar.prospects'), icon: Users, badge: null },
        { id: 'meetings', label: t('admin:backoffice.sidebar.meetings'), icon: Calendar },
    ];
}

export function getBottomItems(t: (key: string) => string): NavItem[] {
    return [
        { id: 'settings', label: t('admin:backoffice.sidebar.settings'), icon: Settings },
        { id: 'help', label: t('admin:backoffice.sidebar.help'), icon: HelpCircle },
    ];
}

interface ClientLite {
    company_name?: string | null;
}

interface DashboardSidebarProps {
    activeTab: string;
    onTabChange: (id: string) => void;
    client?: ClientLite | null;
    onCollapsedChange?: (collapsed: boolean) => void;
    badges?: Record<string, number | null | undefined>;
    defaultCollapsed?: boolean;
    /** `fixed`: the desktop column (lg and up). `drawer`: inside the mobile menu drawer. */
    variant?: 'fixed' | 'drawer';
}

export default function DashboardSidebar({ activeTab, onTabChange, client, onCollapsedChange, badges, defaultCollapsed = false, variant = 'fixed' }: DashboardSidebarProps) {
    const { t } = useTranslation(['admin', 'dialer']);
    const drawer = variant === 'drawer';
    const NAV_ITEMS = getNavItems(t);
    const BOTTOM_ITEMS = getBottomItems(t);
    const [collapsedState, setCollapsed] = useState(!!defaultCollapsed);
    const collapsed = drawer ? false : collapsedState;
    useEffect(() => { if (!drawer) onCollapsedChange?.(!!defaultCollapsed); }, []);  // eslint-disable-line react-hooks/exhaustive-deps
    const [contactOpen, setContactOpen] = useState(false);
    const companyName = client?.company_name || BRAND.name;

    const itemClass = (isActive: boolean) => cn(
        'w-full flex items-center px-3 rounded-md text-sm transition-colors',
        drawer ? 'min-h-11' : 'py-2',
        collapsed ? 'justify-center gap-0' : 'gap-3',
        isActive ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700 hover:bg-gray-100',
    );

    const renderItem = (item: NavItem, withBadge: boolean) => {
        const Icon = item.icon;
        const isActive = activeTab === item.id;
        const count = withBadge ? badges?.[item.id] : null;
        return (
            <button
                key={item.id}
                type="button"
                onClick={() => onTabChange(item.id)}
                title={item.label}
                aria-label={collapsed ? item.label : undefined}
                aria-current={isActive ? 'page' : undefined}
                className={itemClass(isActive)}
            >
                <div className="relative">
                    <Icon className={cn('w-4 h-4', isActive ? 'text-blue-600' : 'text-gray-500')} aria-hidden />
                    {collapsed && !!count && <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-red-500" />}
                </div>
                {!collapsed && item.label}
                {!collapsed && !!count && (
                    <span className="ml-auto bg-red-100 text-red-700 text-xs px-1.5 py-0.5 rounded-full">{count}</span>
                )}
            </button>
        );
    };

    return (
        <aside
            aria-label={t('admin:backoffice.sidebar.navLabel')}
            className={cn(
                'flex-col bg-white',
                drawer
                    ? 'flex h-full w-full'
                    : cn('hidden lg:flex border-r border-gray-200 min-h-screen fixed left-0 top-0 z-30 transition-[width] duration-200 motion-reduce:transition-none', collapsed ? 'w-16' : 'w-56'),
            )}
        >
            {/* Brand: folds the column on desktop */}
            <div className="p-4">
                {drawer ? (
                    <div className="flex items-center gap-2 px-1">
                        <div className="w-7 h-7 rounded bg-black flex items-center justify-center">
                            <span className="text-white font-bold text-xs">{BRAND.name.charAt(0)}</span>
                        </div>
                        <span className="font-semibold text-gray-900 text-sm truncate">{companyName}</span>
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={() => { const next = !collapsed; setCollapsed(next); onCollapsedChange?.(next); }}
                        title={companyName}
                        aria-label={collapsed ? t('dialer:nav.openMenu') : companyName}
                        aria-expanded={!collapsed}
                        className={cn(
                            'flex items-center w-full hover:bg-gray-50 rounded-lg p-1 -ml-1 transition-colors',
                            collapsed ? 'justify-center' : 'gap-2',
                        )}
                    >
                        <div className="w-7 h-7 rounded bg-black flex items-center justify-center">
                            <span className="text-white font-bold text-xs">{BRAND.name.charAt(0)}</span>
                        </div>
                        {!collapsed && (
                            <>
                                <div className="flex-1 text-left min-w-0">
                                    <span className="font-semibold text-gray-900 text-sm truncate block">{companyName}</span>
                                </div>
                                <ChevronLeft className="w-4 h-4 text-gray-400" aria-hidden />
                            </>
                        )}
                    </button>
                )}
            </div>

            <nav className="flex-1 px-3 py-2 space-y-0.5">
                {NAV_ITEMS.map((item) => renderItem(item, true))}
            </nav>

            <div className="px-3 py-3 border-t border-gray-200 space-y-0.5">
                {BOTTOM_ITEMS.map((item) => renderItem(item, false))}
                <button
                    type="button"
                    onClick={() => setContactOpen(true)}
                    title={t('dialer:contactKevin.title')}
                    aria-label={collapsed ? t('dialer:contactKevin.button') : undefined}
                    data-testid="contact-kevin-sidebar"
                    className={itemClass(false)}
                >
                    <MessageSquare className="w-4 h-4 text-gray-500" aria-hidden />
                    {!collapsed && t('dialer:contactKevin.button')}
                </button>
            </div>
            <ContactKevinModal open={contactOpen} onOpenChange={setContactOpen} />
        </aside>
    );
}
